import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BottomButton, Page } from '../../components';
import { applyDiscounts, calculatePriceInCart, findCourier, formatBillingAddress, formatShippingAddress, getAllUrlParams, estimateCartDeliveryDate } from '../../libs';
import { userActions } from '../../store/actions';
import { CardAddress, CardSimple, CardIconTextLink, CardDiscounts } from '../../ui';
import { LocalShippingOutlined, ChevronRight, HouseOutlined, PriceCheckOutlined, LocalOfferOutlined } from '@mui/icons-material';

export function CompleteOrder() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { products, discount } = useSelector(state => state.cartData);
    const { couriers, subCategoryDict, productionTimeData, courierKanguryPrice } = useSelector(state => state.mainData);
    const { shippingAddresses, billingAddresses } = useSelector((state) => state.userData);
    const defaultShippingAddress = shippingAddresses?.length > 0 ? shippingAddresses.find(item => item.isDefault) : null;
    const defaultBillingAddress = billingAddresses?.length > 0 ? billingAddresses.find(item => item.isDefault) : null;
    const [totalPrice, setTotalPrice] = React.useState(0);
    const { askBill } = getAllUrlParams();
    const [isAskingBill, setIsAskingBill] = React.useState(askBill === 'true');
    const [isNoCloud, setIsNoCloud] = React.useState(false);

    const totalItems = products.reduce((acc, item) => acc + item.countInCart * item.totalImages, 0);

    React.useEffect(() => {
        dispatch(userActions.getShippingAddresses());
        dispatch(userActions.getBillingAddresses());
    }, [dispatch]);

    const handleCompleteOrder = React.useCallback(() => {
        navigate(`/upload-order?askBill=${isAskingBill}&noCloud=${isNoCloud}`);
    }, [isAskingBill, isNoCloud, navigate]);

    React.useEffect(() => {
        let totalPrice = 0;
        if (products && products.length > 0 && subCategoryDict) {
            products.forEach((item) => {
                totalPrice += calculatePriceInCart({ product: item, subCategoryDict, products, noCloud: isNoCloud });
            });
            setTotalPrice(totalPrice);
        }
    }, [discount, isNoCloud, products, subCategoryDict]);

    const shippingRows = formatShippingAddress(defaultShippingAddress);

    let billingRows = formatBillingAddress(defaultBillingAddress);

    const courier = React.useMemo(() => {
        const country = defaultShippingAddress?.country;
        const state = defaultShippingAddress?.state;
        const itemsPrice = applyDiscounts(discount, totalPrice, 0).total;
        return findCourier({ couriers, country, totalPrice: itemsPrice, state })
    }, [couriers, defaultShippingAddress?.country, defaultShippingAddress?.state, discount, totalPrice]);

    // Check if user can go to next step
    const enableNextButton = React.useMemo(() => {
        return defaultShippingAddress && (isAskingBill ? defaultBillingAddress : true) && courier;
    }, [defaultShippingAddress, isAskingBill, defaultBillingAddress, courier]);

    const finalCourierPrice = React.useCallback(() => {
        // Check if there is at least one product with categoryId !== 1000000
        const hasPhysicalProduct = products.some(product => product.categoryId !== 1000000);
        if (hasPhysicalProduct) {
            return courierKanguryPrice;
        }
        return 0;
    }, [courierKanguryPrice, products]);

    const [showedPrice] = React.useMemo(() => {
        const resultsDiscounts = applyDiscounts(discount, totalPrice);
        const showKanguryPrice = resultsDiscounts.total + finalCourierPrice()
        return [showKanguryPrice];
    }, [discount, finalCourierPrice, totalPrice]);

    const discountRows = React.useMemo(() => {
        let rows = [];
        const _totalPrice = totalPrice;
        if (discount) {
            discount.forEach((item) => {
                if (item.credits < _totalPrice) {
                    rows.push({
                        title: item.description,
                        text: `Sconto: ${item.credits.toFixed(2).replace(".", ",")} €`,
                    });
                } else {
                    rows.push({
                        title: item.description,
                        text: `Sconto: ${_totalPrice.toFixed(2).replace(".", ",")} €`,
                    });
                }
            });
        }
        return rows
    }, [discount, totalPrice]);

    const dateFormatted = React.useMemo(() => {
        if (products && subCategoryDict && productionTimeData && courier && products.length > 0) {
            const date = estimateCartDeliveryDate(products, subCategoryDict, productionTimeData, courier)
            if (date)
                return date.toLocaleDateString('it-IT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
        }
        return null;
    }, [products, subCategoryDict, productionTimeData, courier]);

    if (products.length === 0) {
        navigate('/cart');
        return;
    }

    return (
        <Page title="Concludi Ordine"
            customBottomSection={
                <BottomButton otherClass={"p-1"}
                    onClick={handleCompleteOrder}
                    disabled={!enableNextButton}
                    content={
                        <React.Fragment>
                            Prosegui
                            <ChevronRight className='ms-2' />
                        </React.Fragment>
                    }
                    contentUp={
                        <div className='d-flex justify-content-between align-items-center fw-bold w-100 ps-4 pe-4 pt-1'>
                            <div>Totale (IVA incl.):</div>
                            <div className='text-primary'>{showedPrice.toFixed(2).replace(".", ",")} €</div>
                        </div>
                    }>
                </BottomButton>
            }>
            <div className='p-2'>
                <p className='fw-bold'>Indirizzo di Consegna</p>

                {defaultShippingAddress ?
                    <CardAddress
                        title={defaultShippingAddress.recipient}
                        rows={shippingRows}
                        note={defaultShippingAddress.courierNotes}
                        icon={<HouseOutlined fontSize='large' />}
                        link={'/shipping-addresses?check=true'}
                        linkText={'Cambia Indirizzo'}
                    />
                    :
                    <CardIconTextLink
                        icon={<HouseOutlined fontSize='large' />}
                        link={'/shipping-addresses?index=-1'}
                        linkText={'Aggiungi un nuovo indirizzo'}
                        title={"Nessun indirizzo selezionato"}
                    />}


                {/* TODO uncomment when we will have the official company 
                <p className='fw-bold'>Indirizzo di Fatturazione</p>
                <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="askBill"
                        checked={isAskingBill}
                        onChange={(e) => {
                            setIsAskingBill(e.target.checked)
                            // add params to url using navigate without incrementing history
                            navigate({ search: `?${e.target.checked ? 'askBill=true' : ''}` }, { replace: true });
                        }} />
                    <label className="form-check-label" htmlFor="askBill">
                        Richiedi Fattura
                    </label>
                </div> */}
                {isAskingBill && (
                    defaultBillingAddress ?
                        <CardAddress
                            title={defaultShippingAddress.recipient}
                            rows={billingRows}
                            note={defaultShippingAddress.courierNotes}
                            icon={<PriceCheckOutlined fontSize='large' />}
                            link={'/billing-addresses?check=true'}
                            linkText={'Modifica Dati'}
                        />
                        : <CardIconTextLink
                            icon={<PriceCheckOutlined fontSize='large' />}
                            link={'/billing-addresses?index=-1'}
                            linkText={'Aggiungi un nuovo indirizzo'}
                            title={"Nessun indirizzo di fatturazione selezionato"}
                        />)}

                <p className='fw-bold'>Metodo di Spedizione</p>
                {courier ?
                    <CardIconTextLink
                        centerTitle={false}
                        darkText={true}
                        icon={<LocalShippingOutlined fontSize='large' />}
                        title={courier.name}
                        text={"Prezzo: " + finalCourierPrice() + " €"}
                        footer={
                            <div className='d-flex align-items-center'>
                                <div className=''>Consegna stimata: {dateFormatted}</div>
                            </div>}
                    />
                    : <CardIconTextLink
                        icon={<LocalShippingOutlined fontSize='large' />}
                        title={defaultShippingAddress ? "Indirizzo non raggiungiungibile al momento, inserirne un altro"
                            : "Specificare un indirizzo per informazioni sui metodi di spedizione"}
                        danger={true}
                    />}

                <p className='fw-bold mt-3'>Codici Sconto</p>
                {discountRows.length > 0 ?
                    <CardDiscounts
                        link={`/discount-code?totalPrice=${totalPrice}&courierPrice=${courier?.price || 0}&totalItems=${totalItems}`}
                        rows={discountRows}
                    />
                    :
                    <CardIconTextLink
                        icon={<LocalOfferOutlined fontSize='large' />}
                        link={`/discount-code?totalPrice=${totalPrice}&courierPrice=${courier?.price || 0}&totalItems=${totalItems}`}
                        linkText={'Aggiungi Codice'}
                        title={"Nessun sconto applicato"}
                    />}
                <p className='fw-bold mt-3'>No Cloud</p>
                <div className={`card rounded-4 overflow-hidden shadow m-3`}>
                    <div className="card-body text-center p-2">
                        Se abilitato non verrà creato un album per ciascuna foto stampata, attiva l'opzione soltanto per ricevere delle foto provviste di tag Kangury da associare ad un tuo album già esistente.
                        <div className='form-check form-switch text-center d-flex justify-content-center align-items-center'>
                            <input
                                className="form-check-input cursor-pointer"
                                type="checkbox"
                                onClick={() => setIsNoCloud(!isNoCloud)}
                                value={isNoCloud} checked={isNoCloud}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Page>
    );
}